import { Languages } from '@livv/models';
import { Box, Button, TextField, Typography } from '@mui/material';
import type { FirebaseError } from 'firebase/app';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState, useEffect, useMemo } from 'react';
import Alert from '@components/Alert';
import FullPageLoader from '@components/Loader/FullPageLoader';
import { auth as firebaseAuth } from '@config/firebase/client';
import { useAuth } from '@utils/context/auth';
import useUser from '@utils/context/user';
import { validatePhoneNumber } from '@utils/form';
import useAlertSnackBar from '@utils/hooks/useAlertSnackBar';
import useRecaptcha from '@utils/hooks/useRecaptcha';

export const MFA_ENROLLED_QUERY_KEY = 'mfaEnrolled';

const EnrollSmsMfa: FC = () => {
    const { t } = useTranslation('smsMfa');
    const {
        isLoading: isAuthLoading,
        errors: authError,
        resetErrors,
        sendSmsMfaEnrollment,
        assertSmsCodeMfaEnrollment,
        mfaState,
        logout,
    } = useAuth();
    const { info: user } = useUser();
    const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? '');
    const [isProcessing, setIsProcessing] = useState(false);
    const [successSnackBar, setSuccessMessage] = useAlertSnackBar({ severity: 'success' });
    const [warningSnackBar, setWarningMessage] = useAlertSnackBar({ severity: 'warning' });
    const [verificationCode, setVerificationCode] = useState('');
    const { get: getRecaptcha, clear: clearRecaptcha } = useRecaptcha();
    const router = useRouter();

    const phoneError = useMemo(
        () =>
            typeof validatePhoneNumber(phoneNumber) === 'string'
                ? t(validatePhoneNumber(phoneNumber) as string)
                : '',
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [phoneNumber, t],
    );

    useEffect(() => {
        // This is a fix of an issue in Firebase SDK v10 as described in https://github.com/firebase/firebase-js-sdk/issues/3356
        (window as any).recaptchaVerifier = getRecaptcha(); // eslint-disable-line

        return () => {
            clearRecaptcha();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (authError === 'auth/requires-recent-login') {
            setTimeout(() => logout(true), 4000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authError]);

    const handleSendEnrollmentSms = async () => {
        setIsProcessing(true);
        resetErrors();
        if (!firebaseAuth.currentUser) {
            setWarningMessage(t('noUser'));
            setIsProcessing(false);

            return;
        }
        firebaseAuth.languageCode = router.locale ?? Languages.fr;
        const verificationId = await sendSmsMfaEnrollment(
            firebaseAuth.currentUser,
            phoneNumber,
            getRecaptcha(),
        );
        if (verificationId) {
            setSuccessMessage(t('smsSentSuccess'));
        } else {
            setWarningMessage(t('smsSentError'));
        }
        setIsProcessing(false);
    };

    const handleVerifySmsCodeAndEnroll = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mfaState?.verificationId) {
            setWarningMessage(t('noVerificationId'));

            return;
        }
        if (!firebaseAuth.currentUser) {
            setWarningMessage(t('noUser'));

            return;
        }
        setIsProcessing(true);
        resetErrors();
        try {
            firebaseAuth.languageCode = router.locale ?? Languages.fr;
            const result = await assertSmsCodeMfaEnrollment(
                firebaseAuth.currentUser,
                mfaState?.verificationId,
                verificationCode,
            );
            if (!result) {
                return;
            }
            setVerificationCode('');
            setSuccessMessage(t('enrollment.success'));
            await router.replace(
                {
                    pathname: router.pathname,
                    query: { ...router.query, [MFA_ENROLLED_QUERY_KEY]: 'true' },
                },
                undefined,
                { shallow: true },
            );
            // force reload page because HOC does not get the updated user state
            router.reload();
        } catch (err) {
            console.error(err);
            const errorCode = (err as FirebaseError)?.code;
            setWarningMessage(
                errorCode ? t(`firebaseError:${errorCode}`) : t('enrollment.warning'),
            );

            return;
        } finally {
            setIsProcessing(false);
        }
    };

    if (isAuthLoading) return <FullPageLoader />;

    return (
        <Box alignItems="center" display="flex" flexDirection="column" mt={4}>
            <Typography variant="h1">{t('enrollment.title')}</Typography>
            <Typography sx={{ mb: 2, mt: 2 }}>{t('enrollment.description')}</Typography>
            {authError && (
                <Alert onClose={resetErrors} severity="error" sx={{ mt: 2 }}>
                    {t(`firebaseError:${authError}`)}
                </Alert>
            )}
            {!mfaState?.verificationId ? (
                <>
                    <TextField
                        disabled={isProcessing}
                        error={!!phoneError}
                        helperText={phoneError}
                        label={t('enrollment.phoneLabel')}
                        margin="normal"
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+33612345678"
                        type="tel"
                        value={phoneNumber}
                        fullWidth
                    />
                    <Button
                        color="primary"
                        disabled={isProcessing || !phoneNumber}
                        onClick={handleSendEnrollmentSms}
                        sx={{ mt: 2 }}
                        variant="contained"
                        fullWidth
                    >
                        {isProcessing ? t('sending') : t('enrollment.sendSmsButton')}
                    </Button>
                </>
            ) : (
                <form onSubmit={handleVerifySmsCodeAndEnroll} style={{ width: '100%' }}>
                    <TextField
                        disabled={isProcessing}
                        label={t('verificationCodeLabel')}
                        margin="normal"
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="123456"
                        autoFocus
                        fullWidth
                    />
                    <Button
                        color="primary"
                        disabled={isProcessing || verificationCode.length < 6}
                        sx={{ mt: 2 }}
                        type="submit"
                        variant="contained"
                        fullWidth
                    >
                        {isProcessing ? t('enrollment.enrolling') : t('enrollment.verifyButton')}
                    </Button>
                </form>
            )}
            {successSnackBar}
            {warningSnackBar}
        </Box>
    );
};

export default EnrollSmsMfa;
